import { useState, useEffect } from 'react';
import { Seo, usePageSeo, assetUrl, absUrl } from '../lib/seo';
import { PageSeoContent } from '../components/common/RichContent';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Phone, Shield, Users, Star, Sparkles, Scissors, Activity, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { DoctorCard } from '../components/common/DoctorCard';
import { LogoLoader } from '../components/common/LogoLoader';
import { doctorsAPI, specialitiesAPI } from '../lib/api';
import { getWhatsAppLink, getCategoryColor } from '../lib/utils';

const categoryData = {
  skin: {
    name: 'Skin',
    title: 'Skin Treatments',
    heroTitle: 'Expert Skin Care Specialists',
    description: 'Find the best dermatologists for acne, eczema, pigmentation, and all skin conditions.',
    longDescription: 'Our network of verified skin specialists offers comprehensive care for all dermatological conditions. From common issues like acne and eczema to advanced treatments for pigmentation and skin rejuvenation, find the right expert for your needs.',
    icon: Sparkles,
    bgClass: 'from-amber-100 to-orange-100',
    textColor: 'text-amber-900',
    accentColor: 'bg-amber-500',
    treatments: [
      'Acne Treatment',
      'Eczema & Psoriasis Care',
      'Pigmentation Correction',
      'Skin Cancer Screening',
      'Laser Resurfacing',
      'Anti-aging Treatments',
    ],
    faqs: [
      {
        question: 'What does a dermatologist treat?',
        answer: 'Dermatologists diagnose and treat over 3,000 conditions affecting skin, hair, and nails. Common conditions include acne, eczema, psoriasis, rosacea, skin infections, and skin cancer.'
      },
      {
        question: 'How often should I see a dermatologist?',
        answer: 'For general skin health, an annual checkup is recommended. If you have specific skin conditions or concerns, your dermatologist may recommend more frequent visits.'
      },
      {
        question: 'What should I expect during my first visit?',
        answer: 'During your first visit, the dermatologist will examine your skin, discuss your concerns and medical history, and recommend appropriate treatment options.'
      }
    ]
  },
  hair: {
    name: 'Hair',
    title: 'Hair Solutions',
    heroTitle: 'Advanced Hair Restoration Experts',
    description: 'Connect with leading hair transplant surgeons and trichologists for all hair concerns.',
    longDescription: 'Whether you\'re experiencing hair loss, thinning, or scalp conditions, our expert trichologists and hair restoration specialists provide cutting-edge solutions including PRP therapy, hair transplants, and medical treatments.',
    icon: Scissors,
    bgClass: 'from-gray-800 to-gray-900',
    textColor: 'text-white',
    accentColor: 'bg-gray-600',
    isLight: true,
    treatments: [
      'Hair Loss Diagnosis',
      'PRP Therapy',
      'FUE Hair Transplant',
      'Scalp Micropigmentation',
      'Medical Hair Therapy',
      'Alopecia Treatment',
    ],
    faqs: [
      {
        question: 'What causes hair loss?',
        answer: 'Hair loss can be caused by genetics, hormonal changes, medical conditions, medications, stress, or nutritional deficiencies. A specialist can determine the exact cause through examination and tests.'
      },
      {
        question: 'Is hair transplant permanent?',
        answer: 'Yes, hair transplant results are generally permanent. The transplanted hair follicles are resistant to the hormones that cause hair loss and will continue to grow naturally.'
      },
      {
        question: 'What is PRP therapy?',
        answer: 'PRP (Platelet-Rich Plasma) therapy involves injecting your own concentrated platelets into the scalp to stimulate hair growth and strengthen existing hair follicles.'
      }
    ]
  },
  weight: {
    name: 'Weight',
    title: 'Weight Management',
    heroTitle: 'Medical Weight Loss Programs',
    description: 'Achieve your health goals with medically supervised weight management programs.',
    longDescription: 'Our weight management specialists combine medical expertise with personalized nutrition and lifestyle guidance to help you achieve sustainable weight loss results safely.',
    icon: Activity,
    bgClass: 'from-emerald-100 to-teal-100',
    textColor: 'text-emerald-900',
    accentColor: 'bg-emerald-500',
    treatments: [
      'Medical Weight Assessment',
      'Customized Diet Plans',
      'Metabolic Testing',
      'Non-surgical Fat Reduction',
      'Prescription Weight Management',
      'Lifestyle Modification Programs',
    ],
    faqs: [
      {
        question: 'What is medical weight management?',
        answer: 'Medical weight management involves doctor-supervised programs that may include dietary guidance, behavioral therapy, medications, and regular monitoring for safe, effective weight loss.'
      },
      {
        question: 'How is it different from regular dieting?',
        answer: 'Unlike regular dieting, medical weight management addresses underlying health issues, provides professional monitoring, and offers evidence-based treatments for sustainable results.'
      },
      {
        question: 'Who should consider medical weight management?',
        answer: 'Anyone struggling with weight loss, especially those with BMI over 30, weight-related health conditions, or those who haven\'t succeeded with traditional methods.'
      }
    ]
  },
  aesthetic: {
    name: 'Aesthetic',
    title: 'Aesthetic Procedures',
    heroTitle: 'Premium Aesthetic Treatments',
    description: 'Discover skilled aesthetic practitioners for non-surgical rejuvenation and enhancement.',
    longDescription: 'Our aesthetic medicine specialists offer the latest in non-surgical cosmetic procedures, from injectables to advanced skin treatments, helping you achieve natural-looking results.',
    icon: Award,
    bgClass: 'from-purple-100 to-pink-100',
    textColor: 'text-purple-900',
    accentColor: 'bg-purple-500',
    treatments: [
      'Botox & Dysport',
      'Dermal Fillers',
      'Chemical Peels',
      'Microneedling',
      'Laser Treatments',
      'Thread Lifts',
    ],
    faqs: [
      {
        question: 'Are aesthetic procedures safe?',
        answer: 'When performed by qualified professionals, aesthetic procedures are generally safe. It\'s important to choose verified, experienced practitioners and discuss your medical history.'
      },
      {
        question: 'How long do results last?',
        answer: 'Results vary by treatment. Botox typically lasts 3-4 months, while dermal fillers can last 6 months to 2 years depending on the type and area treated.'
      },
      {
        question: 'What is the recovery time?',
        answer: 'Most non-surgical aesthetic procedures have minimal downtime. You may experience temporary redness or swelling, but can usually return to normal activities the same day.'
      }
    ]
  }
};

export const CategoryPage = () => {
  const seo = usePageSeo();
  const { category } = useParams();
  const [doctors, setDoctors] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(true);

  const data = categoryData[category?.toLowerCase()];
  const Icon = data?.icon || Sparkles;

  useEffect(() => {
    const fetchData = async () => {
      if (!data) return;
      try {
        const [specialitiesRes] = await Promise.all([
          specialitiesAPI.getAll(data.name),
        ]);
        setSpecialities(specialitiesRes.data);
        
        // Fetch doctors with any speciality from this category
        const specIds = specialitiesRes.data.map(s => s.id);
        if (specIds.length > 0) {
          const doctorsRes = await doctorsAPI.getAll({ doctor_status: 'approved', speciality: specIds[0] });
          setDoctors(doctorsRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category, data]);

  if (!data) {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-gray-700 mb-4">Category not found</h2>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-alabaster" data-testid={`category-page-${category}`}>
      <Seo override={seo} title={`${data.title} Specialists & Treatments | DrPortals`} description={data.longDescription} />
      {/* Hero */}
      <section className={`bg-gradient-to-br ${data.bgClass} py-16 md:py-24 px-4`}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className={`inline-flex items-center gap-2 ${data.accentColor} text-white px-4 py-2 rounded-full mb-6`}>
              <Icon className="w-5 h-5" />
              <span className="font-medium">{data.title}</span>
            </div>
            <h1 className={`font-heading text-4xl md:text-5xl font-bold ${data.textColor} mb-6`}>
              {seo.h1 || data.heroTitle}
            </h1>
            <p className={`text-lg ${data.isLight ? 'text-gray-300' : 'text-gray-700'} mb-8`}>
              {data.longDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={getWhatsAppLink(`Hi, I'm looking for ${data.title.toLowerCase()} specialists.`)} target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white px-8 py-6 gap-2 w-full sm:w-auto">
                  <Phone className="w-5 h-5" />
                  Get Help Finding a Doctor
                </Button>
              </a>
              <Link to="/doctors">
                <Button variant={data.isLight ? "secondary" : "outline"} className="rounded-full px-8 py-6 w-full sm:w-auto">
                  Browse All Doctors
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <span className="flex items-center gap-2 text-gray-600">
              <Shield className="w-5 h-5 text-gold" />
              Verified Doctors Only
            </span>
            <span className="flex items-center gap-2 text-gray-600">
              <Users className="w-5 h-5 text-gold" />
              Curated Specialists
            </span>
            <span className="flex items-center gap-2 text-gray-600">
              <Star className="w-5 h-5 text-gold" />
              Premium Care
            </span>
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-navy-900 mb-8 text-center">
            Treatments & Procedures
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {data.treatments.map((treatment) => (
              <div
                key={treatment}
                className="bg-white p-4 rounded-xl border border-gray-100 text-center hover:shadow-md transition-shadow"
              >
                <span className="text-gray-700 font-medium">{treatment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Doctors */}
      <section className="py-16 md:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl font-bold text-navy-900 mb-2">
                {data.title} Specialists
              </h2>
              <p className="text-gray-600">
                Connect with verified experts in {data.title.toLowerCase()}
              </p>
            </div>
            <Link to={`/doctors?category=${data.name}`}>
              <Button variant="outline" className="rounded-full border-navy-800 text-navy-800">
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <LogoLoader />
          ) : doctors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctors.slice(0, 6).map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No specialists found for this category yet.</p>
              <a href={getWhatsAppLink(`Hi, I'm looking for ${data.title.toLowerCase()} specialists.`)} target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white">
                  Get Help via WhatsApp
                </Button>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-navy-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {data.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="bg-white rounded-xl mb-3 border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-medium text-navy-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4 bg-navy-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to Find Your {data.title} Specialist?
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Our team will help you connect with the perfect specialist based on your needs and location.
          </p>
          <a href={getWhatsAppLink(`Hi, I need help finding a ${data.title.toLowerCase()} specialist.`)} target="_blank" rel="noopener noreferrer">
            <Button className="rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white px-10 py-6 text-lg gap-2">
              <Phone className="w-5 h-5" />
              Chat on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": data.faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })
      }} />
      <PageSeoContent seo={seo} />
    </div>
  );
};
